import { useRef } from 'react';
import { TheirInductionFragment } from '@graphqlTypes';
import ArrowTailRight from '@/assets/svg/arrow-tail-right-no-padding.svg';
import GenericImage from '@/components/InducteeCard/generic-person.webp';
import { Container, Image, RevealWrapper, RhLink, SectionHeader } from '@components';

import { useInView, motion } from 'motion/react';
import { AnimVals } from '@/utils/animUtils';
import { useViewSize } from '@/providers/ViewSizeContext';
import { usePageWrapperData } from '@/providers/PageWrapperContext';

import className from 'classnames/bind';
import styles from './ConnectedInductees.module.scss';
const cx = className.bind(styles);

// -------------------- Types

type ConnectedInducteesData = NonNullable<
  NonNullable<TheirInductionFragment['inducteeData']>['connectedInductees']
>;
type InducteeNode = Extract<
  NonNullable<ConnectedInducteesData['inductees']>['nodes'][number],
  { __typename?: 'Inductee' }
>;

// -------------------- Card item

const ConnectedInducteeCard = (props: InducteeNode & { index: number; isInView: boolean }) => {
  const { title, uri, pageData, index, isInView } = props;
  const featuredImage = pageData?.featuredImage;

  // -------------------- Animate in (fade + slide up, staggered) — mirrors InlineRedirect

  const ANIM_DIST = AnimVals.sm.distance;
  const ANIM_OPACITY = AnimVals.opacity;
  const ANIM_EASE = AnimVals.ease;
  const ANIM_DUR = AnimVals.sm.duration;
  const ANIM_DELAY = AnimVals.sm.delay + index * AnimVals.sm.delay;

  // -------------------- Render

  return (
    <motion.div
      className={cx('card-wrap')}
      initial={{ opacity: ANIM_OPACITY, y: ANIM_DIST }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: ANIM_DELAY, duration: ANIM_DUR, ease: ANIM_EASE }}
    >
      <RhLink href={uri || '#'} className={cx('card')}>
        <div className={cx('image')}>
          {featuredImage ? (
            <Image image={featuredImage} />
          ) : (
            <img src={GenericImage.src} alt={title || ''} />
          )}
        </div>
        <div className={cx('content')}>
          <p className={cx('title-lc-s', 'name')}>{title}</p>
          <span className={cx('arrow')}>
            <ArrowTailRight />
          </span>
        </div>
      </RhLink>
    </motion.div>
  );
};

// -------------------- Main Component

export default function ConnectedInductees(props: ConnectedInducteesData) {
  const { listType, inductees } = props;

  // Only render Inductee nodes (the relationship is restricted to inductees, but the
  // connection type is a union — narrow to the Inductee branch for type-safe access).
  const nodes = (inductees?.nodes ?? []).filter(
    (node): node is InducteeNode => node?.__typename === 'Inductee'
  );

  // -------------------- Copy (heading + body) — chosen by the Members/Groups toggle

  const { labels } = usePageWrapperData();
  const copy = labels?.connectedInductees;
  const variant = listType?.[0] === 'groups' ? copy?.groups : copy?.members;
  const headline = variant?.headline || '';
  const subhead = variant?.body || '';

  // -------------------- Animation

  const rootEl = useRef<HTMLDivElement>(null);
  const { isDesktop } = useViewSize();
  const triggerPercent = isDesktop ? AnimVals.lg.triggerPercent : AnimVals.sm.triggerPercent;
  const isInView = useInView(rootEl, { amount: triggerPercent, once: AnimVals.once });

  if (!nodes.length) return null;

  // -------------------- Render

  return (
    <section className={cx('connected-inductees')} ref={rootEl}>
      <Container>
        <RevealWrapper isInView={isInView}>
          <div className={cx('intro')}>
            <SectionHeader headline={headline} subhead={subhead} isRevealEnabled isInView={isInView} />
          </div>
          <div className={cx('grid')}>
            {nodes.map((node, index) => (
              <ConnectedInducteeCard key={index} {...node} index={index} isInView={isInView} />
            ))}
          </div>
        </RevealWrapper>
      </Container>
    </section>
  );
}
